/**
 * ACL Service
 * Handles Access Control List API operations
 */
import { BaseApiService } from '../base/ApiService';
import { getEnvConfig } from '@/app/utils/env';
import {
  AccessControlListResponse,
  AccessControlList,
  UserAccessControlList,
  CsrfTokenData,
  CsrfTokenResponse,
} from '@/shared/types/acl';

/**
 * ACL API Service
 */
class AclService extends BaseApiService {
  constructor() {
    const envConfig = getEnvConfig();
    super({
      baseURL: `${envConfig.API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL}`,
      timeout: envConfig.API_TIMEOUT,
      requiresAuth: true,
    });
  }

  /**
   * Fetch user information and header navigation modules based on the authenticated user's permissions
   *
   * @returns Promise<AccessControlList>
   */
  async getAccessControlList(): Promise<UserAccessControlList> {
    const response = await this.get<AccessControlListResponse>('/user-access-list', {});

    const data = response as unknown as AccessControlListResponse;

    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch access control list');
    }

    return data.result;
  }

  /**
   * Fetch Sub-module Access Control List for the authenticated user by item code
   *
   * @param itemCode - The code of the item to fetch access control for
   * @returns Promise<AccessControlList>
   */
  async getAccessControlByItemCode(
    code?: string
  ): Promise<AccessControlList> {
    const response = await this.get<AccessControlListResponse>('/user-access-list', {
      params: { code },
    });

    const data = response as unknown as AccessControlListResponse;

    if (!data.success || !data.result) {
      throw new Error(data.message || 'Failed to fetch access control list');
    }

    return data.result;
  }

  /**
   * Fetch a CSRF token from the security endpoint.
   *
   * @param expirationDateTime - Desired token expiration as a Unix timestamp (seconds).
   * @returns Promise<CsrfTokenData>
   */
  async getCsrfToken(
    expirationDateTime: number
  ): Promise<CsrfTokenData> {

    if (!Number.isInteger(expirationDateTime) || expirationDateTime <= 0) {
      throw new Error('A valid expiration Unix timestamp in seconds is required');
    }

    const response = await this.get<CsrfTokenResponse>('/user-access-list', {
      params: { expirationDateTime, code: 'security' },
    });

    const data = response as unknown as CsrfTokenResponse;

    if (!data.success || !data.result?.security?.csrf_token) {
      throw new Error(data.message || 'Failed to fetch CSRF token');
    }

    return data.result.security;
  }
}

// Export singleton instance
export const aclService = new AclService();
